import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronRight, Folder, FolderOpen, Home } from 'lucide-react';
import type { KnowledgeBaseItem } from '@/services/knowledgeBase.service';
import { buildTree, type TreeNode } from './FolderTree';

interface MoveItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: KnowledgeBaseItem | null;
  items: KnowledgeBaseItem[];
  onMove: (itemId: string, newParentId: string | null) => void;
  moving?: boolean;
}

function getDescendantIds(items: KnowledgeBaseItem[], rootId: string): Set<string> {
  const ids = new Set<string>();
  const queue = [rootId];
  while (queue.length > 0) {
    const current = queue.pop()!;
    for (const item of items) {
      if (item.parent_id === current && !ids.has(item.id)) {
        ids.add(item.id);
        queue.push(item.id);
      }
    }
  }
  return ids;
}

function FolderPickerNode({
  node,
  depth,
  selectedId,
  disabledIds,
  onSelect,
}: {
  node: TreeNode;
  depth: number;
  selectedId: string | null;
  disabledIds: Set<string>;
  onSelect: (id: string | null) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const isDisabled = disabledIds.has(node.id);
  const isSelected = selectedId === node.id;
  const folderChildren = node.children.filter(c => c.item_type === 'folder');

  return (
    <div>
      <div
        className={cn(
          'flex items-center gap-1.5 px-2 py-1 rounded-md text-sm cursor-pointer',
          isSelected && 'bg-primary text-primary-foreground',
          !isSelected && !isDisabled && 'hover:bg-muted',
          isDisabled && 'opacity-40 cursor-not-allowed',
        )}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={() => {
          if (!isDisabled) onSelect(node.id);
          if (folderChildren.length > 0) setExpanded(!expanded);
        }}
      >
        {folderChildren.length > 0 ? (
          <ChevronRight
            className={cn(
              'h-3.5 w-3.5 shrink-0 transition-transform',
              expanded && 'rotate-90'
            )}
          />
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        {expanded ? (
          <FolderOpen className="h-4 w-4 shrink-0" />
        ) : (
          <Folder className="h-4 w-4 shrink-0" />
        )}
        <span className="truncate">{node.name}</span>
      </div>
      {expanded && folderChildren.map(child => (
        <FolderPickerNode
          key={child.id}
          node={child}
          depth={depth + 1}
          selectedId={selectedId}
          disabledIds={disabledIds}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

export default function MoveItemDialog({
  open,
  onOpenChange,
  item,
  items,
  onMove,
  moving,
}: MoveItemDialogProps) {
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);

  const disabledIds = useMemo(() => {
    if (!item) return new Set<string>();
    const ids = getDescendantIds(items, item.id);
    ids.add(item.id);
    return ids;
  }, [item, items]);

  const tree = useMemo(() => buildTree(items), [items]);
  const folderTree = useMemo(
    () => tree.filter(n => n.item_type === 'folder'),
    [tree]
  );

  const handleMove = () => {
    if (!item) return;
    onMove(item.id, selectedParentId);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Move "{item?.name}"</DialogTitle>
          <DialogDescription>
            Choose a destination folder.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="max-h-64 overflow-auto border rounded-md py-1">
            <div
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 rounded-md text-sm cursor-pointer mx-1',
                selectedParentId === null && 'bg-primary text-primary-foreground',
                selectedParentId !== null && 'hover:bg-muted',
              )}
              onClick={() => setSelectedParentId(null)}
            >
              <Home className="h-4 w-4 shrink-0" />
              <span>Root</span>
            </div>
            {folderTree.map(node => (
              <FolderPickerNode
                key={node.id}
                node={node}
                depth={1}
                selectedId={selectedParentId}
                disabledIds={disabledIds}
                onSelect={id => setSelectedParentId(id)}
              />
            ))}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleMove} disabled={moving}>
            {moving ? 'Moving...' : 'Move'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
